import type React from 'react';
import type { CSSProperties } from 'react';
import { PreviewProxyStyle, PreviewProxyWrapper } from 'src/components/CommonStyles/Proxy/PreviewStyles';
import PDFPreview from 'src/components/PDF/PDFPreview';
import {
  AUDIO,
  IMAGE,
  MEDIA_PATH,
  PDF,
  PROXY_OFFLINE,
  SAMPLE_DOWNLOAD_LINK,
  SHARED_MEDIA_PATH,
  VIDEO,
} from 'src/constants';
import MediaUnavailable from '../MediaUnavailable';
import ImagePreview from './ImagePreview/ImagePreview';
import VideoPreview from './VideoPreview';

interface FormatData {
  id?: string;
  online?: boolean;
}

interface MediaPreviewRouterProps {
  id?: string | null;
  format?: FormatData;
  mimetype?: string;
  isShared?: boolean;
  itemType?: string | null;
  nextFormat?: FormatData;
  prevFormat?: FormatData;
  isTransitioning?: boolean;
  onTransitionEnd?: () => void;
}

const MediaPreviewRouter: React.FC<MediaPreviewRouterProps> = ({
  id = null,
  format,
  mimetype,
  isShared = false,
  itemType = null,
  nextFormat,
  prevFormat,
  isTransitioning,
  onTransitionEnd,
}) => {
  const getMediaPath = (fmt: FormatData | undefined): string | null =>
    fmt?.id ? `${isShared ? SHARED_MEDIA_PATH : MEDIA_PATH}/${fmt.id}` : null;

  if (!format?.online)
    return (
      <PreviewProxyStyle>
        <PreviewProxyWrapper>
          <MediaUnavailable text={PROXY_OFFLINE} />
        </PreviewProxyWrapper>
      </PreviewProxyStyle>
    );

  const containerStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    opacity: isTransitioning ? 0 : 1,
    transition: 'opacity 0.3s ease-in-out',
  };

  const handleTransitionEnd = () => {
    if (isTransitioning && onTransitionEnd) {
      onTransitionEnd();
    }
  };

  if (mimetype?.includes(IMAGE))
    return (
      <div style={containerStyle} onTransitionEnd={handleTransitionEnd}>
        <ImagePreview
          src={`${isShared ? SHARED_MEDIA_PATH : MEDIA_PATH}/${format?.id}`}
          nextSrc={getMediaPath(nextFormat)}
          prevSrc={getMediaPath(prevFormat)}
        />
      </div>
    );

  if (mimetype?.includes(PDF))
    return (
      <div style={containerStyle} onTransitionEnd={handleTransitionEnd}>
        <PDFPreview
          pdfUrl={`${MEDIA_PATH}/${format?.id}`}
          nextPdfUrl={getMediaPath(nextFormat) ?? undefined}
          prevPdfUrl={getMediaPath(prevFormat) ?? undefined}
          downloadLink={SAMPLE_DOWNLOAD_LINK}
          isOnMediaPage={false}
        />
      </div>
    );

  if (id && itemType && (mimetype?.includes(VIDEO) || mimetype?.includes(AUDIO)))
    return (
      <div style={containerStyle} onTransitionEnd={handleTransitionEnd}>
        <VideoPreview
          mimetype={mimetype}
          proxyID={format?.id || ''}
          nextProxyId={nextFormat?.id}
          prevProxyId={prevFormat?.id}
          itemID={id}
          itemType={itemType}
          isShared={isShared}
        />
      </div>
    );

  return (
    <PreviewProxyStyle>
      <PreviewProxyWrapper>
        <MediaUnavailable text={'Unsupported Mimetype'} />
      </PreviewProxyWrapper>
    </PreviewProxyStyle>
  );
};

export default MediaPreviewRouter;
